/**
 * Appointment Step Navigation JavaScript
 * Handles navigation between booking steps and step-related functionality
 */

// Global variable to store selected appointment information
let selectedAppointmentData = null;

// Session storage functions for selected appointment - using centralized utility
// These functions are now provided by appointment-session-storage.js
// Keeping function names for backward compatibility

// Handle time slot selection and transition to package selection
function handleTimeSlotSelection(timeSlot, screening) {
    const appointmentTimeText = timeSlot.textContent.trim();

    // Find the actual appointment data
    const appointment = screening.appointments?.find(apt => {
        const formattedTime = apt.appointmentTime ? formatTime(apt.appointmentTime) : 'Time TBD';
        return formattedTime === appointmentTimeText;
    });

    if (!appointment) {
        return;
    }

    // Format the data as requested
    const date = screening.screeningDate ? screening.screeningDate.split("T")[0] : null;
    const formattedDate = date ? formatDate(date) : 'Date TBD';
    const time = appointmentTimeText;
    const location = screening.siteName || 'Unknown Location';
    const address1 = screening.address1 || '';
    const address2 = screening.address2 || '';
    const cityStateZip = `${screening.city || ''}, ${screening.state || ''} ${screening.zipCode || ''}`;
    const fullAddress = [address1, address2, cityStateZip].filter(Boolean).join(', ');
    const appointmentId = appointment.appointmentCellGuid || appointment.appointmentId || appointment.id || null;

    // Save the selected appointment data
    selectedAppointmentData = {
        date: formattedDate,
        time: time,
        location: location,
        address: fullAddress,
        _appointment_id: appointmentId,
        // Additional data for internal use
        screening: screening,
        appointment: appointment
    };

    // Save to session storage
    saveSelectedAppointmentToSession(selectedAppointmentData);

    // Transition to package selection step
    showPackageSelection();
}

// Show package selection step
function showPackageSelection() {
    // Hide appointment content
    const appointmentContent = document.getElementById('appointment-content');
    if (appointmentContent) {
        appointmentContent.style.display = 'none';
    }

    // Hide zip code fallback if visible
    const zipFallback = document.getElementById('zip-code-fallback');
    if (zipFallback) {
        zipFallback.style.display = 'none';
    }

    // Hide membership selection if visible
    const membershipSelection = document.getElementById('membership-selection');
    if (membershipSelection) {
        membershipSelection.style.display = 'none';
    }

    // Show package selection
    const packageSelection = document.getElementById('select-package');
    if (packageSelection) {
        packageSelection.style.display = 'block';

        // Initialize package selection functionality
        setTimeout(() => {
            if (typeof initializePackageSelection === 'function') {
                initializePackageSelection();
            }

            // Load saved gender selection when showing step 2
            if (typeof loadSavedGenderSelection === 'function') {
                loadSavedGenderSelection();
            }
        }, 100);
    }

    // Update progress steps
    updateProgressSteps(2); // Step 2: Select Package
}


// Show appointment selection step (back from package selection)
function showAppointmentSelection() {
    // Clear selected appointment data and session storage
    clearSelectedAppointmentFromSession();
    selectedAppointmentData = null;

    // Clear gender selection when going back to step 1
    if (typeof window.AppointmentPackages?.clearGenderSelection === 'function') {
        window.AppointmentPackages.clearGenderSelection();
    }

    // Hide package selection
    const packageSelection = document.getElementById('select-package');
    if (packageSelection) {
        packageSelection.style.display = 'none';
    }

    // Hide membership selection if visible
    const membershipSelection = document.getElementById('membership-selection');
    if (membershipSelection) {
        membershipSelection.style.display = 'none';
    }

    // Show appointment content
    const appointmentContent = document.getElementById('appointment-content');
    if (appointmentContent) {
        appointmentContent.style.display = 'grid';
    }

    // Always show the full screening list (not detailed view) and reset any expanded states
    if (typeof showFullScreeningList === 'function') {
        showFullScreeningList();
    }

    // Reset any expanded appointment time slots to collapsed state
    resetExpandedTimeSlots();

    // Update progress steps
    updateProgressSteps(1); // Step 1: Location & Time
}

// Package selection functionality is now handled in appointment-package-selection.js

// Update progress steps based on current step
function updateProgressSteps(currentStep) {
    const progressSteps = document.querySelectorAll('.progress-step');

    progressSteps.forEach((step, index) => {
        const stepNumber = index + 1;

        if (stepNumber < currentStep) {
            // Completed steps
            step.classList.add('completed');
            step.classList.remove('active');
        } else if (stepNumber === currentStep) {
            // Current active step
            step.classList.add('active');
            step.classList.remove('completed');
        } else {
            // Future steps
            step.classList.remove('active', 'completed');
        }
    });

    // Setup click handlers for completed steps (allow going back only)
    setupProgressStepNavigation(currentStep);
}

// Setup click handlers for progress step navigation
function setupProgressStepNavigation(currentStep) {
    const progressSteps = document.querySelectorAll('.progress-step');

    progressSteps.forEach((step, index) => {
        const stepNumber = index + 1;

        // Remove existing event listeners
        step.replaceWith(step.cloneNode(true));
        const newStep = document.querySelectorAll('.progress-step')[index];

        // Only allow clicking on completed steps (going back)
        if (stepNumber < currentStep) {
            newStep.addEventListener('click', function() {
                handleProgressStepClick(stepNumber);
            });

            // Add accessibility attributes
            newStep.setAttribute('role', 'button');
            newStep.setAttribute('tabindex', '0');
            newStep.setAttribute('aria-label', `Go back to ${newStep.querySelector('.step-label').textContent}`);

            // Add keyboard support
            newStep.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleProgressStepClick(stepNumber);
                }
            });
        } else {
            // Remove accessibility attributes for non-clickable steps
            newStep.removeAttribute('role');
            newStep.removeAttribute('tabindex');
            newStep.removeAttribute('aria-label');
        }
    });
}

// Handle progress step clicks (navigation back to previous steps)
function handleProgressStepClick(targetStep) {
    switch(targetStep) {
        case 1:
            // Go back to Location & Time step
            showAppointmentSelection();
            break;
        case 2:
            // Go back to Select Package step (if implemented)
            if (typeof showPackageSelection === 'function') {
                showPackageSelection();
            }
            break;
        case 3:
            // Go back to OneLIfe Membership step (if implemented)
            // This would be implemented when step 3 is added
            break;
        default:
            break;
    }
}

// Check for saved appointment data and restore appropriate step
function checkForSavedAppointmentData() {
    const savedAppointment = loadSelectedAppointmentFromSession();

    if (savedAppointment) {
        // Restore the selected appointment data
        selectedAppointmentData = savedAppointment;

        // Show package selection step since user had already selected an appointment
        showPackageSelection();
    }
}

// Expose functions globally
window.showAppointmentSelection = showAppointmentSelection;
window.showPackageSelection = showPackageSelection;

// Clear appointment data when starting new zip code search - using centralized utility
// This function is now provided by appointment-session-storage.js

// Reset any expanded appointment time slots to collapsed state
function resetExpandedTimeSlots() {
    try {
        // Reset all expanded time slots to collapsed
        const expandedTimeSlots = document.querySelectorAll('.appointment-time-slots:not(.collapsed)');
        expandedTimeSlots.forEach(timeSlots => {
            if (!timeSlots.classList.contains('expanded')) { // Don't collapse detailed view slots
                timeSlots.classList.add('collapsed');
            }
        });

        // Reset all "Show More" buttons to "Show More" state
        const expandedButtons = document.querySelectorAll('.show-more-appointments.expanded');
        expandedButtons.forEach(button => {
            button.classList.remove('expanded');
            const textSpan = button.querySelector('.text');
            if (textSpan) {
                textSpan.textContent = 'Show More';
            }
        });
    } catch (error) {
        // Silent error handling
    }
}
